import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Code01, Plus, Trash02 } from "@untitledui/icons";
import { useId, useMemo, useState } from "react";
import {
	DialogTrigger as AriaDialogTrigger,
	Heading as AriaHeading,
} from "react-aria-components";
import { toast } from "sonner";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toast } from "@/components/Molecules/Toast/Toast";
import { ConditionDateValueInput } from "@/components/Organisms/shared/ConditionDateValueInput";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { formDefinitionQueryOptions } from "@/services/form-definition";
import { jobQueryOptions } from "@/services/job/job.options";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import { organizationQueryOptions } from "@/services/organization";
import { recordQueryOptions } from "@/services/record/record.options";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";
import { useCreateWorkflowRule } from "@/services/workflow";
import { workflowQueryOptions } from "@/services/workflow/workflow.options";
import { WorkflowApiError } from "@/services/workflow/workflow.service";
import {
	WorkflowActionType,
	WorkflowTriggerType,
} from "@/services/workflow/workflow.types";
import { formatOperatorLabel } from "@/utils/workflow-rules/format";
import {
	buildConditionCompareFields,
	isNumericConditionDataType,
	operatorsForDataType,
	RULE_FORM_CONDITION_FIELDS,
	RULE_FORM_NO_VALUE_OPERATORS,
} from "@/utils/workflow-rules/workflow-rule-form-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

type ActionTypeValue =
	(typeof WorkflowActionType)[keyof typeof WorkflowActionType];

interface ConditionRow {
	id: string;
	fieldName: string;
	fieldLabel: string;
	dataType: string;
	operator: string;
	value: string;
	validValues?: string[];
}

type ActionParams =
	| { type: "set_status"; statusId: string }
	| { type: "set_assignee"; assignee: string; assigneeName?: string }
	| {
			type: "send_email";
			recipientType: "Assignee" | "Owner" | "Custom" | "";
			recipientEmail: string;
	  }
	| {
			type: "create_entity";
			entityClass: "asset" | "record" | "job" | "ticket" | "";
			entityTypeId: string;
			entityName: string;
			priorityId: string;
	  }
	| { type: "" };

interface ActionRow {
	id: string;
	type: ActionTypeValue | "";
	params: ActionParams;
}

type TriggerType = "" | "entity_created" | "status_entered" | "status_exited";

interface Draft {
	name: string;
	description: string;
	triggerType: TriggerType;
	triggerStatusId: string;
	conditions: ConditionRow[];
	actions: ActionRow[];
}

export interface CreateWorkflowRuleModalProps {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	handleClose: () => void;
	entityKind: "JobType" | "TicketType" | "RecordType" | "AssetType";
	entityTypeId: string;
	mode: "platform" | "organization";
	organizationId?: string;
	lifecycleId?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_REGEX =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Renders the appropriate value input based on the condition field's data type. */
function ConditionValueInput({
	dataType,
	value,
	onChange,
	validValues,
	t,
}: {
	dataType: string;
	value: string;
	onChange: (v: string) => void;
	validValues?: string[];
	t: ReturnType<typeof useAppTranslation>["t"];
}) {
	const dt = dataType.toLowerCase();

	if (validValues && validValues.length > 0) {
		return (
			<Select
				label={t("admin.workflowRules.create.conditions.value.label")}
				selectedKey={value || null}
				onSelectionChange={(key) => onChange(key as string)}
				placeholder={t(
					"admin.workflowRules.create.conditions.value.placeholder",
				)}
				size="sm"
			>
				{validValues.map((v) => (
					<SelectItem key={v} id={v} label={v} />
				))}
			</Select>
		);
	}
	if (dt === "boolean") {
		return (
			<div className="flex items-center gap-2 py-1">
				<span className="text-sm font-medium text-secondary">
					{t("admin.workflowRules.create.conditions.value.label")}
				</span>
				<Checkbox
					isSelected={value === "true"}
					onChange={(checked) => onChange(checked ? "true" : "false")}
					aria-label={t("admin.workflowRules.create.conditions.value.label")}
				/>
			</div>
		);
	}
	if (dt === "date" || dt === "datetime") {
		return (
			<ConditionDateValueInput
				label={t("admin.workflowRules.create.conditions.value.label")}
				value={value}
				onChange={onChange}
				showTime={dt === "datetime"}
			/>
		);
	}
	if (dt === "number" || dt === "integer" || dt === "decimal") {
		const trimmed = value.trim();
		const numericInvalid =
			trimmed !== "" && !Number.isFinite(Number(trimmed));
		return (
			<Input
				label={t("admin.workflowRules.create.conditions.value.label")}
				placeholder={t(
					"admin.workflowRules.create.conditions.value.placeholder",
				)}
				value={value}
				onChange={onChange}
				inputMode="numeric"
				isInvalid={numericInvalid}
				hint={
					numericInvalid
						? t("admin.workflowRules.create.conditions.value.invalidNumber")
						: undefined
				}
				size="sm"
			/>
		);
	}
	return (
		<Input
			label={t("admin.workflowRules.create.conditions.value.label")}
			placeholder={t("admin.workflowRules.create.conditions.value.placeholder")}
			value={value}
			onChange={onChange}
			size="sm"
		/>
	);
}

function makeEmptyAction(): ActionRow {
	return { id: crypto.randomUUID(), type: "", params: { type: "" } };
}

function makeEmptyCondition(): ConditionRow {
	return {
		id: crypto.randomUUID(),
		fieldName: "",
		fieldLabel: "",
		dataType: "",
		operator: "",
		value: "",
	};
}

function makeDraft(): Draft {
	return {
		name: "",
		description: "",
		triggerType: "",
		triggerStatusId: "",
		conditions: [],
		actions: [makeEmptyAction()],
	};
}

interface ValidationResult {
	ok: boolean;
	errors: Record<string, string>;
}

function validateDraft(draft: Draft, entityKind: string): ValidationResult {
	const errors: Record<string, string> = {};

	if (!draft.name.trim()) errors.name = "Name is required";
	if (!draft.triggerType) errors.trigger = "Trigger is required";

	if (
		(draft.triggerType === "status_entered" ||
			draft.triggerType === "status_exited") &&
		!draft.triggerStatusId
	) {
		errors.triggerStatus = "Status is required";
	}

	draft.conditions.forEach((cond, i) => {
		const needsValue = !RULE_FORM_NO_VALUE_OPERATORS.has(cond.operator);
		if (
			!cond.fieldName ||
			!cond.operator ||
			(needsValue && !cond.value.trim())
		) {
			errors[`condition_${i}`] =
				"Complete or remove this condition to continue";
			return;
		}
		if (
			needsValue &&
			isNumericConditionDataType(cond.dataType) &&
			!Number.isFinite(Number(cond.value.trim()))
		) {
			errors[`condition_${i}`] = "Enter a valid number";
		}
	});

	if (draft.actions.length === 0) {
		errors.actions = "At least one action is required";
	}

	draft.actions.forEach((action, i) => {
		if (!action.type) {
			errors[`action_${i}_type`] = "Action type is required";
			return;
		}
		if (action.type === "set_status") {
			const p = action.params as Extract<ActionParams, { type: "set_status" }>;
			if (!p.statusId)
				errors[`action_${i}_statusId`] = "Target status is required";
		} else if (action.type === "set_assignee") {
			const p = action.params as Extract<
				ActionParams,
				{ type: "set_assignee" }
			>;
			if (!p.assignee?.trim())
				errors[`action_${i}_assignee`] = "Assignee is required";
		} else if (action.type === "send_email") {
			const p = action.params as Extract<ActionParams, { type: "send_email" }>;
			if (!p.recipientType)
				errors[`action_${i}_recipientType`] = "Recipient type is required";
			if (p.recipientType === "Custom") {
				if (!p.recipientEmail?.trim())
					errors[`action_${i}_recipientEmail`] = "Recipient email is required";
				else if (!EMAIL_REGEX.test(p.recipientEmail.trim()))
					errors[`action_${i}_recipientEmail`] = "Enter a valid email address";
			}
		} else if (action.type === "create_entity") {
			const p = action.params as Extract<
				ActionParams,
				{ type: "create_entity" }
			>;
			if (!p.entityClass)
				errors[`action_${i}_entityClass`] = "Entity class is required";
			if (p.entityClass && p.entityClass !== "asset" && !p.entityTypeId)
				errors[`action_${i}_entityTypeId`] = "Entity type is required";
			if (!p.entityName?.trim())
				errors[`action_${i}_entityName`] = "Entity name is required";
			if (p.entityClass === "ticket" && !p.priorityId?.trim())
				errors[`action_${i}_priorityId`] = "Priority is required";
		}
		if (entityKind === "RecordType" && action.type === "set_status") {
			errors[`action_${i}_type`] =
				"Set Status is not available for Record Types";
		}
	});

	return { ok: Object.keys(errors).length === 0, errors };
}

// ─── Small presentational helpers ────────────────────────────────────────────

function SectionHeader({
	title,
	description,
}: {
	title: string;
	description: string;
}) {
	return (
		<div>
			<p className="text-sm font-semibold text-primary">{title}</p>
			<p className="text-xs text-tertiary">{description}</p>
		</div>
	);
}

function InlineError({ message }: { message?: string }) {
	if (!message) return null;
	return <p className="mt-1 text-xs text-error-600">{message}</p>;
}

// ─── CreateWorkflowRuleModal ──────────────────────────────────────────────────

export const CreateWorkflowRuleModal = ({
	isOpen,
	setIsOpen,
	handleClose,
	entityKind,
	entityTypeId,
	mode,
	organizationId,
	lifecycleId,
}: CreateWorkflowRuleModalProps) => {
	const { t } = useAppTranslation();
	const toastId = useId();
	const [draft, setDraft] = useState<Draft>(makeDraft);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const createMutation = useCreateWorkflowRule();

	// lifecycle statuses for job/ticket triggers
	const lifecycleQuery = useQuery({
		...lifecycleQueryOptions.statusLifecycleDetail(
			lifecycleId ?? "",
			organizationId,
		),
		enabled: Boolean(lifecycleId) && (lifecycleId?.length ?? 0) > 0,
	});
	const lifecycleStatuses = lifecycleQuery.data?.statuses ?? [];

	// entity-type queries for "Create Entity" action
	const createEntityClasses = new Set(
		draft.actions
			.filter((a) => a.type === "create_entity")
			.map(
				(a) =>
					(a.params as Extract<ActionParams, { type: "create_entity" }>)
						.entityClass,
			),
	);

	const recordTypesQuery = useQuery({
		...recordQueryOptions.typesAll({ OrganizationId: organizationId }),
		enabled: createEntityClasses.has("record"),
	});
	const jobTypesQuery = useQuery({
		...jobQueryOptions.typesAll({ organizationId }),
		enabled: createEntityClasses.has("job"),
	});
	const ticketTypesQuery = useQuery({
		...ticketQueryOptions.typesAll({ organizationId }),
		enabled: createEntityClasses.has("ticket"),
	});
	const ticketPrioritiesQuery = useQuery({
		...ticketQueryOptions.priorities({ organizationId }),
		enabled: createEntityClasses.has("ticket"),
	});
	const ticketPriorities = ticketPrioritiesQuery.data ?? [];

	// Form definition fields — appended to the static condition field list
	const formDefParams = useMemo(() => {
		if (!entityTypeId) return undefined;
		if (entityKind === "JobType") return { JobTypeId: entityTypeId };
		if (entityKind === "TicketType") return { TicketTypeId: entityTypeId };
		if (entityKind === "AssetType") return { AssetTypeId: entityTypeId };
		if (entityKind === "RecordType") return { RecordTypeId: entityTypeId };
		return undefined;
	}, [entityKind, entityTypeId]);

	const formDefQuery = useQuery({
		...formDefinitionQueryOptions.current(formDefParams, { organizationId }),
		enabled: !!formDefParams,
	});

	const customConditionFields = useMemo(
		() =>
			(formDefQuery.data?.fieldDefinitions ?? [])
				.filter((f): f is typeof f & { label: string } => !!f.label)
				.map((f) => ({
					id: f.id,
					label: f.label,
					dataType: f.dataType,
					validValues:
						Array.isArray(f.validationRules?.validValues) &&
						f.validationRules.validValues.length > 0
							? (f.validationRules.validValues as string[])
							: undefined,
				})),
		[formDefQuery.data],
	);

	const conditionOperatorsQuery = useQuery(
		workflowQueryOptions.conditionOperatorsByDataType,
	);
	const operatorsByDataType = useMemo<Record<string, string[]>>(() => {
		const map: Record<string, string[]> = {};
		for (const item of conditionOperatorsQuery.data ?? []) {
			map[item.dataType] = item.operators;
		}
		return map;
	}, [conditionOperatorsQuery.data]);

	const hasSetAssigneeAction = draft.actions.some(
		(a) => a.type === "set_assignee",
	);
	const orgUsersQuery = useQuery({
		...organizationQueryOptions.users.list(organizationId ?? "", 1, 100),
		enabled:
			mode === "organization" && !!organizationId && hasSetAssigneeAction,
	});
	const orgUsers = orgUsersQuery.data ?? [];

	// draft helpers
	const updateDraft = (patch: Partial<Draft>) =>
		setDraft((prev) => ({ ...prev, ...patch }));

	const updateCondition = (id: string, patch: Partial<ConditionRow>) =>
		setDraft((prev) => ({
			...prev,
			conditions: prev.conditions.map((c) =>
				c.id === id ? { ...c, ...patch } : c,
			),
		}));

	const removeCondition = (id: string) =>
		setDraft((prev) => ({
			...prev,
			conditions: prev.conditions.filter((c) => c.id !== id),
		}));

	const updateAction = (id: string, patch: Partial<ActionRow>) =>
		setDraft((prev) => ({
			...prev,
			actions: prev.actions.map((a) => (a.id === id ? { ...a, ...patch } : a)),
		}));

	const removeAction = (id: string) =>
		setDraft((prev) => ({
			...prev,
			actions: prev.actions.filter((a) => a.id !== id),
		}));

	const handleActionTypeChange = (actionId: string, type: string) => {
		const newType = type as ActionTypeValue | "";
		let params: ActionParams = { type: "" };
		if (newType === "set_status") params = { type: "set_status", statusId: "" };
		else if (newType === "set_assignee")
			params = { type: "set_assignee", assignee: "" };
		else if (newType === "send_email")
			params = { type: "send_email", recipientType: "", recipientEmail: "" };
		else if (newType === "create_entity")
			params = {
				type: "create_entity",
				entityClass: "",
				entityTypeId: "",
				entityName: "",
				priorityId: "",
			};
		updateAction(actionId, { type: newType, params });
	};

	const updateActionParams = (id: string, params: ActionParams) =>
		updateAction(id, { params });

	// submit
	const handleSubmit = async () => {
		const validation = validateDraft(draft, entityKind);
		if (!validation.ok) {
			setErrors(validation.errors);
			return;
		}
		setErrors({});
		setSubmitError(null);

		const triggerConfig: Record<string, unknown> =
			draft.triggerType === "status_entered" ||
			draft.triggerType === "status_exited"
				? { statusId: draft.triggerStatusId }
				: {};

		const entityTypeMap: Record<string, string> = {
			JobType: "job",
			TicketType: "ticket",
			RecordType: "record",
			AssetType: "asset",
		};

		const payload = {
			name: draft.name.trim(),
			description: draft.description.trim() || null,
			entityType: entityTypeMap[entityKind] ?? entityKind,
			organizationId: organizationId ?? null,
			jobTypeId: entityKind === "JobType" ? entityTypeId : null,
			ticketTypeId: entityKind === "TicketType" ? entityTypeId : null,
			recordTypeId: entityKind === "RecordType" ? entityTypeId : null,
			assetTypeId: entityKind === "AssetType" ? entityTypeId : null,
			trigger: { type: draft.triggerType, config: triggerConfig },
			condition:
				draft.conditions.length > 0
					? {
							groupType: "all",
							conditions: draft.conditions.map((c) => ({
								...(UUID_REGEX.test(c.fieldName)
									? {
											columnDefinitionId: c.fieldName,
											entityPropertyName: null,
										}
									: {
											columnDefinitionId: null,
											entityPropertyName: c.fieldName,
										}),
								operator: c.operator,
								...buildConditionCompareFields(c.dataType, c.operator, c.value),
							})),
						}
					: null,
			actions: draft.actions.map((action) => {
				if (action.type === "set_status") {
					const p = action.params as Extract<
						ActionParams,
						{ type: "set_status" }
					>;
					return { type: action.type, config: { statusId: p.statusId } };
				}
				if (action.type === "set_assignee") {
					const p = action.params as Extract<
						ActionParams,
						{ type: "set_assignee" }
					>;
					return {
						type: action.type,
						config: {
							userId: p.assignee.trim(),
							...(p.assigneeName ? { assigneeName: p.assigneeName } : {}),
						},
					};
				}
				if (action.type === "send_email") {
					const p = action.params as Extract<
						ActionParams,
						{ type: "send_email" }
					>;
					return {
						type: action.type,
						config: {
							recipientType: p.recipientType,
							...(p.recipientType === "Custom"
								? { emails: [p.recipientEmail.trim()] }
								: {}),
						},
					};
				}
				if (action.type === "create_entity") {
					const p = action.params as Extract<
						ActionParams,
						{ type: "create_entity" }
					>;
					return {
						type: action.type,
						config: {
							entityTypeClass: p.entityClass,
							entityTypeId: p.entityTypeId || null,
							nameTemplate: p.entityName.trim(),
							...(p.entityClass === "ticket" && p.priorityId?.trim()
								? { priorityId: p.priorityId.trim() }
								: {}),
						},
					};
				}
				return { type: action.type, config: {} };
			}),
		};

		try {
			await createMutation.mutateAsync({ payload, organizationId });
			toast.custom(
				(toastIdParam) => (
					<Toast
						id={toastId}
						color="success"
						addonText="Success"
						message={t("admin.workflowRules.create.toast.success")}
						showCloseButton
						onClose={() => toast.dismiss(toastIdParam)}
					/>
				),
				{ duration: 5000 },
			);
			setDraft(makeDraft());
			setSubmitError(null);
			setErrors({});
			handleClose();
		} catch (err) {
			const serverErrors =
				err instanceof WorkflowApiError ? (err.serverErrors ?? []) : [];
			setSubmitError(
				serverErrors.length > 0
					? serverErrors.join(" ")
					: err instanceof Error
						? err.message
						: t("admin.workflowRules.create.errors.generic"),
			);
		}
	};

	const onClose = () => {
		handleClose();
		setDraft(makeDraft());
		setSubmitError(null);
		setErrors({});
		createMutation.reset();
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			setDraft(makeDraft());
			setSubmitError(null);
			setErrors({});
			createMutation.reset();
		}
	};

	const validation = validateDraft(draft, entityKind);
	const isSubmitDisabled = !validation.ok || createMutation.isPending;
	const hasError = Boolean(submitError);

	const actionTypeOptions = [
		{
			id: WorkflowActionType.setStatus,
			label: t("admin.workflowRules.create.actions.type.setStatus"),
		},
		{
			id: WorkflowActionType.setAssignee,
			label: t("admin.workflowRules.create.actions.type.setAssignee"),
		},
		{
			id: WorkflowActionType.sendEmail,
			label: t("admin.workflowRules.create.actions.type.sendEmail"),
		},
		{
			id: WorkflowActionType.createEntity,
			label: t("admin.workflowRules.create.actions.type.createEntity"),
		},
	].filter(
		(opt) =>
			!(entityKind === "RecordType" && opt.id === "set_status") &&
			!(
				entityKind === "AssetType" && opt.id === WorkflowActionType.setAssignee
			) &&
			!(
				entityKind === "RecordType" && opt.id === WorkflowActionType.setAssignee
			),
	);

	const triggerOptions =
		entityKind === "RecordType"
			? [
					{
						id: WorkflowTriggerType.entityCreated,
						label: t(
							"admin.workflowRules.create.fields.trigger.options.entityCreated",
						),
					},
				]
			: [
					{
						id: WorkflowTriggerType.entityCreated,
						label: t(
							"admin.workflowRules.create.fields.trigger.options.entityCreated",
						),
					},
					{
						id: WorkflowTriggerType.statusEntered,
						label: t(
							"admin.workflowRules.create.fields.trigger.options.statusEntered",
						),
					},
					{
						id: WorkflowTriggerType.statusExited,
						label: t(
							"admin.workflowRules.create.fields.trigger.options.statusExited",
						),
					},
				];

	return (
		<AriaDialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
			<ModalOverlay isDismissable={false}>
				<Modal>
					<Dialog>
						<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-lg">
							{/* Header */}
							<div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
								<div className="relative mx-auto mb-2 w-max">
									{hasError ? (
										<Avatar
											size="2xl"
											placeholderIcon={({ className }) => (
												<AlertCircle
													className={className ?? ""}
													size={32}
													style={{ stroke: "var(--Error-600, #D92D20)" }}
												/>
											)}
											className="bg-[#FEE4E2]! outline-none!"
										/>
									) : (
										<Avatar
											size="2xl"
											placeholderIcon={({ className }) => (
												<Code01
													className={className ?? ""}
													size={32}
													style={{ stroke: "#2563EB" }}
												/>
											)}
											className="border! border-[#BFDBFE]! bg-[#EFF6FF]! outline-none!"
										/>
									)}
								</div>
								<div className="z-10 flex flex-col gap-0.5 text-center">
									<AriaHeading
										slot="title"
										className="text-md font-semibold text-primary"
									>
										{hasError
											? t("admin.workflowRules.create.errors.generic")
											: t("admin.workflowRules.create.modal.title")}
									</AriaHeading>
									<p className="text-sm text-tertiary">
										{hasError
											? (submitError ??
												t("admin.workflowRules.create.modal.description"))
											: t("admin.workflowRules.create.modal.description")}
									</p>
								</div>
							</div>

							{/* Scrollable body */}
							<div className="max-h-[60vh] overflow-y-auto px-4 pb-2 pt-5 sm:px-6">
								<div className="flex flex-col gap-5">
									{/* Name */}
									<div>
										<Input
											label={t("admin.workflowRules.create.fields.name.label")}
											placeholder={t(
												"admin.workflowRules.create.fields.name.placeholder",
											)}
											value={draft.name}
											autoFocus
											isRequired
											onChange={(v) => {
												updateDraft({ name: v });
												setErrors((prev) => {
													const next = { ...prev };
													delete next.name;
													return next;
												});
											}}
											isInvalid={Boolean(errors.name)}
										/>
										<InlineError message={errors.name} />
									</div>

									{/* Description */}
									<TextArea
										label={t(
											"admin.workflowRules.create.fields.description.label",
										)}
										placeholder={t(
											"admin.workflowRules.create.fields.description.placeholder",
										)}
										value={draft.description}
										onChange={(v) => updateDraft({ description: v })}
									/>

									{/* Trigger */}
									<div>
										<Select
											label={t(
												"admin.workflowRules.create.fields.trigger.label",
											)}
											isRequired
											selectedKey={draft.triggerType || null}
											onSelectionChange={(key) => {
												const val = key as TriggerType;
												updateDraft({
													triggerType: val,
													triggerStatusId:
														val === "entity_created"
															? ""
															: draft.triggerStatusId,
												});
												setErrors((prev) => {
													const next = { ...prev };
													delete next.trigger;
													delete next.triggerStatus;
													return next;
												});
											}}
											placeholder={t(
												"admin.workflowRules.create.fields.trigger.placeholder",
											)}
											isInvalid={Boolean(errors.trigger)}
										>
											{triggerOptions.map((opt) => (
												<SelectItem
													key={opt.id}
													id={opt.id}
													label={opt.label}
												/>
											))}
										</Select>
										<p className="mt-1 text-xs text-tertiary">
											{t("admin.workflowRules.create.fields.trigger.help")}
										</p>
										<InlineError message={errors.trigger} />
									</div>

									{/* Target Status (conditional) */}
									{(draft.triggerType === "status_entered" ||
										draft.triggerType === "status_exited") && (
										<div>
											<Select
												label={
													draft.triggerType === "status_entered"
														? t(
																"admin.workflowRules.create.fields.targetStatus.entered",
															)
														: t(
																"admin.workflowRules.create.fields.targetStatus.exited",
															)
												}
												isRequired
												selectedKey={draft.triggerStatusId || null}
												onSelectionChange={(key) => {
													updateDraft({ triggerStatusId: key as string });
													setErrors((prev) => {
														const next = { ...prev };
														delete next.triggerStatus;
														return next;
													});
												}}
												placeholder={t(
													"admin.workflowRules.create.fields.targetStatus.placeholder",
												)}
												isInvalid={Boolean(errors.triggerStatus)}
												isDisabled={lifecycleStatuses.length === 0}
											>
												{lifecycleStatuses.map((s) => (
													<SelectItem
														key={s.id}
														id={s.id}
														label={s.name ?? s.id}
													/>
												))}
											</Select>
											<p className="mt-1 text-xs text-tertiary">
												{t(
													"admin.workflowRules.create.fields.targetStatus.help",
												)}
											</p>
											<InlineError message={errors.triggerStatus} />
										</div>
									)}

									{/* Conditions */}
									<div className="rounded-lg border border-secondary p-4">
										<SectionHeader
											title={t("admin.workflowRules.create.conditions.title")}
											description={t(
												"admin.workflowRules.create.conditions.description",
											)}
										/>
										<div className="mt-3 flex flex-col gap-3">
											{draft.conditions.length === 0 ? (
												<p className="text-xs text-tertiary">
													{t("admin.workflowRules.create.conditions.empty")}
												</p>
											) : (
												draft.conditions.map((cond, i) => (
													<div
														key={cond.id}
														className="flex flex-col gap-2 rounded-md border border-secondary p-3"
													>
														<div className="flex items-start gap-2">
															<div className="flex-1">
																<Select
																	label={t(
																		"admin.workflowRules.create.conditions.field.label",
																	)}
																	selectedKey={cond.fieldName || null}
																	onSelectionChange={(key) => {
																		const allFields = [
																			...RULE_FORM_CONDITION_FIELDS,
																			...customConditionFields,
																		];
																		const found = allFields.find(
																			(f) => f.id === key,
																		);
																		updateCondition(cond.id, {
																			fieldName: key as string,
																			fieldLabel: found?.label ?? "",
																			dataType: found?.dataType ?? "",
																			operator: "",
																			value:
																				found?.dataType?.toLowerCase() ===
																				"boolean"
																					? "false"
																					: "",
																			validValues:
																				found && "validValues" in found
																					? (found.validValues as
																							| string[]
																							| undefined)
																					: undefined,
																		});
																	}}
																	placeholder={t(
																		"admin.workflowRules.create.conditions.field.placeholder",
																	)}
																	size="sm"
																>
																	{[
																		...RULE_FORM_CONDITION_FIELDS.filter(
																			(f) =>
																				!(
																					(entityKind === "AssetType" ||
																						entityKind === "RecordType") &&
																					f.id === "assignee"
																				),
																		),
																		...customConditionFields,
																	].map((f) => (
																		<SelectItem
																			key={f.id}
																			id={f.id}
																			label={f.label}
																		/>
																	))}
																</Select>
															</div>
															<div className="flex-1">
																<Select
																	label={t(
																		"admin.workflowRules.create.conditions.operator.label",
																	)}
																	selectedKey={cond.operator || null}
																	onSelectionChange={(key) => {
																		const op = key as string;
																		updateCondition(cond.id, {
																			operator: op,
																			value: RULE_FORM_NO_VALUE_OPERATORS.has(op)
																				? ""
																				: cond.value,
																		});
																	}}
																	placeholder={t(
																		"admin.workflowRules.create.conditions.operator.placeholder",
																	)}
																	isDisabled={
																		operatorsForDataType(
																			cond.dataType,
																			operatorsByDataType,
																		) === null
																	}
																	size="sm"
																>
																	{(
																		operatorsForDataType(
																			cond.dataType,
																			operatorsByDataType,
																		) ?? []
																	).map((op) => (
																		<SelectItem
																			key={op}
																			id={op}
																			label={formatOperatorLabel(op, t)}
																		/>
																	))}
																</Select>
															</div>
															<button
																type="button"
																className="mt-5 shrink-0 rounded-md p-1 text-tertiary hover:bg-secondary-subtle hover:text-primary"
																aria-label={t(
																	"admin.workflowRules.create.conditions.remove",
																)}
																onClick={() => removeCondition(cond.id)}
															>
																<Trash02 size={16} />
															</button>
														</div>

														{!RULE_FORM_NO_VALUE_OPERATORS.has(cond.operator) && (
															<ConditionValueInput
																dataType={cond.dataType}
																value={cond.value}
																onChange={(v) =>
																	updateCondition(cond.id, { value: v })
																}
																validValues={cond.validValues}
																t={t}
															/>
														)}

														<InlineError message={errors[`condition_${i}`]} />
													</div>
												))
											)}

											{draft.conditions.length < 5 && (
												<Button
													type="button"
													color="secondary"
													size="sm"
													iconLeading={Plus}
													onClick={() =>
														setDraft((prev) => ({
															...prev,
															conditions: [
																...prev.conditions,
																makeEmptyCondition(),
															],
														}))
													}
												>
													{t("admin.workflowRules.create.conditions.add")}
												</Button>
											)}

											{draft.conditions.length > 0 && (
												<p className="text-xs text-tertiary">
													{t(
														"admin.workflowRules.create.conditions.allMustBeTrue",
													)}
												</p>
											)}
										</div>
									</div>

									{/* Actions */}
									<div className="rounded-lg border border-secondary p-4">
										<SectionHeader
											title={t("admin.workflowRules.create.actions.title")}
											description={t(
												"admin.workflowRules.create.actions.description",
											)}
										/>
										<InlineError message={errors.actions} />
										<div className="mt-3 flex flex-col gap-3">
											{draft.actions.map((action, i) => (
												<ActionRowBuilder
													key={action.id}
													action={action}
													index={i}
													canRemove={draft.actions.length > 1}
													mode={mode}
													actionTypeOptions={actionTypeOptions}
													lifecycleStatuses={lifecycleStatuses}
													recordTypes={recordTypesQuery.data ?? []}
													jobTypes={jobTypesQuery.data ?? []}
													ticketTypes={ticketTypesQuery.data ?? []}
													ticketPriorities={ticketPriorities}
													orgUsers={orgUsers}
													errors={errors}
													onTypeChange={(type) =>
														handleActionTypeChange(action.id, type)
													}
													onParamsChange={(params) =>
														updateActionParams(action.id, params)
													}
													onRemove={() => removeAction(action.id)}
													t={t}
												/>
											))}

											{draft.actions.length < 5 && (
												<Button
													type="button"
													color="secondary"
													size="sm"
													iconLeading={Plus}
													onClick={() =>
														setDraft((prev) => ({
															...prev,
															actions: [...prev.actions, makeEmptyAction()],
														}))
													}
												>
													{t("admin.workflowRules.create.actions.add")}
												</Button>
											)}
										</div>
									</div>
								</div>
							</div>

							{/* Footer */}
							<div className="flex flex-col-reverse gap-3 border-t border-secondary p-4 *:grow sm:grid sm:grid-cols-2 sm:p-6">
								<Button
									type="button"
									color="secondary"
									size="lg"
									onClick={onClose}
								>
									{t("common.actions.cancel")}
								</Button>
								<Button
									type="button"
									color="primary"
									size="lg"
									isDisabled={isSubmitDisabled}
									showTextWhileLoading
									isLoading={createMutation.isPending}
									onClick={() => void handleSubmit()}
								>
									{createMutation.isPending
										? t("common.actions.creating")
										: t("common.actions.create")}
								</Button>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
};

// ─── ActionRowBuilder ─────────────────────────────────────────────────────────

interface ActionRowBuilderProps {
	action: ActionRow;
	index: number;
	canRemove: boolean;
	mode: "platform" | "organization";
	actionTypeOptions: { id: string; label: string }[];
	lifecycleStatuses: { id: string; name?: string; displayOrder: number }[];
	recordTypes: { id: string; name?: string | null }[];
	jobTypes: { id: string; name?: string | null }[];
	ticketTypes: { id: string; name?: string | null }[];
	ticketPriorities: { id: string; name: string }[];
	orgUsers: {
		userId: string;
		firstName: string;
		lastName: string;
		displayName: string | null;
		email: string;
	}[];
	errors: Record<string, string>;
	onTypeChange: (type: string) => void;
	onParamsChange: (params: ActionParams) => void;
	onRemove: () => void;
	t: (key: string) => string;
}

function ActionRowBuilder({
	action,
	index,
	canRemove,
	mode,
	actionTypeOptions,
	lifecycleStatuses,
	recordTypes,
	jobTypes,
	ticketTypes,
	ticketPriorities,
	orgUsers,
	errors,
	onTypeChange,
	onParamsChange,
	onRemove,
	t,
}: ActionRowBuilderProps) {
	const params = action.params;

	return (
		<div className="flex flex-col gap-3 rounded-md border border-secondary p-3">
			<div className="flex items-start gap-2">
				<div className="flex-1">
					<Select
						label={t("admin.workflowRules.create.actions.type.label")}
						isRequired
						selectedKey={action.type || null}
						onSelectionChange={(key) => onTypeChange(key as string)}
						placeholder={t(
							"admin.workflowRules.create.actions.type.placeholder",
						)}
						isInvalid={Boolean(errors[`action_${index}_type`])}
						size="sm"
					>
						{actionTypeOptions.map((opt) => (
							<SelectItem key={opt.id} id={opt.id} label={opt.label} />
						))}
					</Select>
					<InlineError message={errors[`action_${index}_type`]} />
				</div>

				<button
					type="button"
					className="mt-5 shrink-0 rounded-md p-1 text-tertiary hover:bg-secondary-subtle hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
					aria-label={t("admin.workflowRules.create.actions.remove")}
					disabled={!canRemove}
					onClick={onRemove}
				>
					<Trash02 size={16} />
				</button>
			</div>

			{/* set_status */}
			{action.type === "set_status" &&
				(() => {
					const p = params as Extract<ActionParams, { type: "set_status" }>;
					return (
						<div>
							<Select
								label={t("admin.workflowRules.create.actions.setStatus.label")}
								isRequired
								selectedKey={p.statusId || null}
								onSelectionChange={(key) =>
									onParamsChange({
										type: "set_status",
										statusId: key as string,
									})
								}
								placeholder={t(
									"admin.workflowRules.create.actions.setStatus.placeholder",
								)}
								isInvalid={Boolean(errors[`action_${index}_statusId`])}
								isDisabled={lifecycleStatuses.length === 0}
								size="sm"
							>
								{lifecycleStatuses.map((s) => (
									<SelectItem key={s.id} id={s.id} label={s.name ?? s.id} />
								))}
							</Select>
							<InlineError message={errors[`action_${index}_statusId`]} />
						</div>
					);
				})()}

			{/* set_assignee */}
			{action.type === "set_assignee" &&
				(() => {
					const p = params as Extract<ActionParams, { type: "set_assignee" }>;
					if (mode === "organization") {
						return (
							<>
								<Select
									label={t(
										"admin.workflowRules.create.actions.setAssignee.label",
									)}
									isRequired
									selectedKey={p.assignee || null}
									onSelectionChange={(key) => {
										const userId = key as string;
										const user = orgUsers.find((u) => u.userId === userId);
										const assigneeName = user
											? (user.displayName ??
												`${user.firstName} ${user.lastName}`.trim())
											: undefined;
										onParamsChange({
											type: "set_assignee",
											assignee: userId,
											assigneeName,
										});
									}}
									placeholder={t(
										"admin.workflowRules.create.actions.setAssignee.placeholder",
									)}
									isInvalid={Boolean(errors[`action_${index}_assignee`])}
									isDisabled={orgUsers.length === 0}
									size="sm"
								>
									{orgUsers.map((u) => (
										<SelectItem
											key={u.userId}
											id={u.userId}
											label={u.displayName ?? `${u.firstName} ${u.lastName}`}
											supportingText={u.email}
										/>
									))}
								</Select>
								<InlineError message={errors[`action_${index}_assignee`]} />
							</>
						);
					}
					return (
						<>
							<Input
								label={t(
									"admin.workflowRules.create.actions.setAssignee.label",
								)}
								isRequired
								placeholder={t(
									"admin.workflowRules.create.actions.setAssignee.placeholder",
								)}
								value={p.assignee}
								onChange={(v) =>
									onParamsChange({ type: "set_assignee", assignee: v })
								}
								isInvalid={Boolean(errors[`action_${index}_assignee`])}
								size="sm"
							/>
							<InlineError message={errors[`action_${index}_assignee`]} />
						</>
					);
				})()}

			{/* send_email */}
			{action.type === "send_email" &&
				(() => {
					const p = params as Extract<ActionParams, { type: "send_email" }>;
					return (
						<>
							<div>
								<Select
									label={t(
										"admin.workflowRules.create.actions.sendEmail.recipientType.label",
									)}
									isRequired
									selectedKey={p.recipientType || null}
									onSelectionChange={(key) =>
										onParamsChange({
											...p,
											recipientType: key as "Assignee" | "Owner" | "Custom",
											recipientEmail: key !== "Custom" ? "" : p.recipientEmail,
										})
									}
									placeholder={t(
										"admin.workflowRules.create.actions.sendEmail.recipientType.placeholder",
									)}
									isInvalid={Boolean(errors[`action_${index}_recipientType`])}
									size="sm"
								>
									<SelectItem
										key="Assignee"
										id="Assignee"
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientType.assignee",
										)}
									/>
									<SelectItem
										key="Owner"
										id="Owner"
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientType.owner",
										)}
									/>
									<SelectItem
										key="Custom"
										id="Custom"
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientType.custom",
										)}
									/>
								</Select>
								<InlineError
									message={errors[`action_${index}_recipientType`]}
								/>
							</div>

							{p.recipientType === "Custom" && (
								<div>
									<Input
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientEmail.label",
										)}
										isRequired
										placeholder="email@example.com"
										value={p.recipientEmail}
										type="email"
										onChange={(v) =>
											onParamsChange({ ...p, recipientEmail: v })
										}
										isInvalid={Boolean(
											errors[`action_${index}_recipientEmail`],
										)}
										size="sm"
									/>
									<InlineError
										message={errors[`action_${index}_recipientEmail`]}
									/>
									<p className="mt-1 text-xs text-tertiary">
										{t(
											"admin.workflowRules.create.actions.sendEmail.recipientEmail.help",
										)}
									</p>
								</div>
							)}
						</>
					);
				})()}

			{/* create_entity */}
			{action.type === "create_entity" &&
				(() => {
					const p = params as Extract<ActionParams, { type: "create_entity" }>;
					const entityTypeItems: { id: string; name: string }[] =
						p.entityClass === "record"
							? recordTypes.map((r) => ({ id: r.id, name: r.name ?? r.id }))
							: p.entityClass === "job"
								? jobTypes.map((j) => ({ id: j.id, name: j.name ?? j.id }))
								: p.entityClass === "ticket"
									? ticketTypes.map((tk) => ({
											id: tk.id,
											name: tk.name ?? tk.id,
										}))
									: [];

					return (
						<>
							<div>
								<Select
									label={t(
										"admin.workflowRules.create.actions.createEntity.entityClass.label",
									)}
									isRequired
									selectedKey={p.entityClass || null}
									onSelectionChange={(key) =>
										onParamsChange({
											...p,
											entityClass: key as "asset" | "record" | "job" | "ticket",
											entityTypeId: "",
										})
									}
									placeholder={t(
										"admin.workflowRules.create.actions.createEntity.entityClass.placeholder",
									)}
									isInvalid={Boolean(errors[`action_${index}_entityClass`])}
									size="sm"
								>
									<SelectItem
										key="asset"
										id="asset"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.asset",
										)}
									/>
									<SelectItem
										key="record"
										id="record"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.record",
										)}
									/>
									<SelectItem
										key="job"
										id="job"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.job",
										)}
									/>
									<SelectItem
										key="ticket"
										id="ticket"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.ticket",
										)}
									/>
								</Select>
								<InlineError message={errors[`action_${index}_entityClass`]} />
							</div>

							{p.entityClass && (
								<div>
									{p.entityClass === "asset" ? (
										<>
											<p className="mb-1 text-xs font-medium text-secondary">
												{t(
													"admin.workflowRules.create.actions.createEntity.entityType.label",
												)}
											</p>
											<div className="rounded-lg border border-secondary bg-secondary px-3 py-2 text-xs text-disabled">
												{t(
													"admin.workflowRules.create.actions.createEntity.entityType.assetUnavailable",
												)}
											</div>
										</>
									) : (
										<>
											<Select
												label={t(
													"admin.workflowRules.create.actions.createEntity.entityType.label",
												)}
												isRequired
												selectedKey={p.entityTypeId || null}
												onSelectionChange={(key) =>
													onParamsChange({ ...p, entityTypeId: key as string })
												}
												placeholder={t(
													"admin.workflowRules.create.actions.createEntity.entityType.placeholder",
												)}
												isInvalid={Boolean(
													errors[`action_${index}_entityTypeId`],
												)}
												size="sm"
											>
												{entityTypeItems.map((et) => (
													<SelectItem key={et.id} id={et.id} label={et.name} />
												))}
											</Select>
											<InlineError
												message={errors[`action_${index}_entityTypeId`]}
											/>
										</>
									)}
								</div>
							)}

							<Input
								label={t(
									"admin.workflowRules.create.actions.createEntity.name.label",
								)}
								isRequired
								placeholder={t(
									"admin.workflowRules.create.actions.createEntity.name.placeholder",
								)}
								value={p.entityName}
								maxLength={255}
								onChange={(v) => onParamsChange({ ...p, entityName: v })}
								isInvalid={Boolean(errors[`action_${index}_entityName`])}
								size="sm"
							/>
							<InlineError message={errors[`action_${index}_entityName`]} />

							{p.entityClass === "ticket" && (
								<>
									<Select
										label={t(
											"admin.workflowRules.create.actions.createEntity.priorityId.label",
										)}
										isRequired
										selectedKey={p.priorityId || null}
										onSelectionChange={(key) =>
											onParamsChange({ ...p, priorityId: key as string })
										}
										placeholder={t(
											"admin.workflowRules.create.actions.createEntity.priorityId.placeholder",
										)}
										isInvalid={Boolean(errors[`action_${index}_priorityId`])}
										isDisabled={ticketPriorities.length === 0}
										size="sm"
									>
										{ticketPriorities.map((pr) => (
											<SelectItem key={pr.id} id={pr.id} label={pr.name} />
										))}
									</Select>
									<InlineError message={errors[`action_${index}_priorityId`]} />
								</>
							)}
						</>
					);
				})()}
		</div>
	);
}
